import _ from 'lodash'
import calendars from 'scoreboard/components/calendars'
import formatValue from 'scoreboard/components/format-value'
import globals from 'scoreboard/components/globals'
import i18n from 'scoreboard/components/i18n'
import isGeographicOrRollup from 'scoreboard/datasets/is-geographic-or-rollup'
import RelativeOptions from 'scoreboard/components/relative-options'
import translateBlank from './translate-blank'
import blankMessage from './blank-message'
import { getBreakdownPeriodLabel }
	from 'scoreboard/components/designer/breakdown-period-options'
import notInAnyRangeMessage from 'scoreboard/datasets/not-in-any-range-message'
import ChartCalendarLabels
	from 'scoreboard/components/designer/chart-calendar-labels'
const { customCalendarLabels } = ChartCalendarLabels
import { getGroupCalendar, getBreakdownCalendar } from './group-or-breakdown-calendar'
import DataType from 'scoreboard/datasets/model/data-type'
const { YES_NO } = DataType

/**
 * Returns the label/sublabel for a given breakdown range
 *
 * @param {ChartData} data
 * @param {ChartDesignerBreakdown} breakdown
 * @param {ChartDesignerRange} range
 *
 * @returns {{label: string, sublabel: string}}
 */
function breakdownRangeLabels({ data, breakdown, range}) {
	let breakdownBy = breakdown.get('breakdownBy')

	let label, sublabel
	if (range.get('type') === 'non-matching') {
		label = i18n.get('common.other')
	} else if (breakdownBy === 'every-value' || breakdownBy === 'specific-values') {
		label = datasetValueRangeLabel({ data, breakdown, range })
	} else if (breakdownBy === 'every-range' || breakdownBy === 'specific-ranges') {
		label = datasetRangeRangeLabel({ data, breakdown, range })
	} else if (breakdownBy === 'calendar-period-ranges') {
		({ label, sublabel } = calendarPeriodRangeLabels({ range }))
	} else if (breakdown.isCalendarPeriodGroupBreakdown()) {
		({ label, sublabel } = groupedCalendarPeriodRangeLabels({ range }))
	}

	return { label, sublabel }
}

function datasetValueRangeLabel ({ data, breakdown, range }) {
	let datasetId = breakdown.get('datasetId')
	let datasetFieldId = breakdown.get('datasetFieldId')

	if (datasetId == null || datasetFieldId == null) {
		return ''
	}

	let dataset = data.datasetModel(datasetId)
	let field = dataset.getUnderlyingField(datasetFieldId)
	var dataType = field.dataType

	let value = range.get('value')

	// If the value is the blank placeholder value, then return the translated
	// Blank message regardless of dataType
	let translated = translateBlank(value)
	if (value !== translated) {
		return translated
	}

	if (dataType === YES_NO) {
		if (value === 'yes') {
			return i18n.get('common.yes')
		} else if (value === 'no') {
			return i18n.get('common.no')
		}
	} else if (isGeographicOrRollup(dataType)) {
		// #40583 show tree item label instead of its id
		const treeItem = data.treeItems(value)
		if (treeItem && treeItem.label) {
			value = treeItem.label
		}
	} else {
		return formatValue({
			value,
			dataType
		})
	}

	return value
}

/**
 * Returns the label for a breakdown range that's based on a dataset range
 */
function datasetRangeRangeLabel ({ data, breakdown, range }) {
	let dataset = data.datasetModel(breakdown.get('datasetId'))
	let field = dataset.getUnderlyingField(breakdown.get('datasetFieldId'))

	let datasetRangeType = range.get('datasetRangeType')
	if (datasetRangeType == 'standard') {
		let datasetRangeId = range.get('datasetRangeId')
		let datasetRange = _.find(field.ranges, r => r.id === datasetRangeId)
		return datasetRange ? datasetRange.label : rangeId
	} else if (datasetRangeType == 'blank') {
		return blankMessage()
	} else if (datasetRangeType == 'not-in-any') {
		return notInAnyRangeMessage(field)
	}
}

function calendarPeriodRangeLabels ({ range }) {
	let calendarLabel, startPeriodLabel, endPeriodLabel
	if (!_.isNil(range.get('startPeriodId'))) {
		let startPeriod = calendars.periodsById[range.get('startPeriodId')]
		let endPeriod = calendars.periodsById[range.get('endPeriodId')]

		calendarLabel = startPeriod.calendar.label
		startPeriodLabel = startPeriod.label
		endPeriodLabel = endPeriod.label
	} else {
		let calendarId = range.get('calendarId')
		const calendar = calendarId && calendars.calendarsById[calendarId] ||
			calendars.periodsById[globals.periodId].calendar
		if (calendarId) {
			calendarLabel = calendar.label
		} else {
			calendarLabel = i18n.get('charts.chart.currentCalendar')
		}

		let relative = new RelativeOptions()
		// #39787 if we are using the current calendar, make sure the labels are
		// using generic terms
		const calendarToUse = calendarId ? calendar : null
		startPeriodLabel = relative.label(range.get('startPeriodDelta'), calendarToUse)
		endPeriodLabel = relative.label(range.get('endPeriodDelta'), calendarToUse)
	}

	return {
		label: calendarLabel,
		// Use the unicode en dash character (appropriate for ranges)
		sublabel: startPeriodLabel + '\u2013' + endPeriodLabel
	}
}

function groupedCalendarPeriodRangeLabels ({ range }) {
	let groupCalendar = getGroupCalendar(range.toJSON())
	let breakdownCalendar = getBreakdownCalendar(range.toJSON())

	const groupLabel = customCalendarLabels[groupCalendar.type] ?
		i18n.get(customCalendarLabels[groupCalendar.type]) : groupCalendar.label
	const breakdownLabel = customCalendarLabels[breakdownCalendar.type] ?
		i18n.get(customCalendarLabels[breakdownCalendar.type]) : breakdownCalendar.label

	let label = groupLabel + ' ' + i18n.get('charts.range.dataBy')
		+ ' ' + breakdownLabel

	const groupCalendarType = groupCalendar.type
	const breakdownCalendarType = breakdownCalendar.type
	const startLabel = getBreakdownPeriodLabel({
		groupCalendarType, breakdownCalendarType,
		index: range.get('startPeriodIndex'),
		periodDelta: range.get('startPeriodDelta')
	})
	const endLabel = getBreakdownPeriodLabel({
		groupCalendarType, breakdownCalendarType,
		index: range.get('endPeriodIndex'),
		periodDelta: range.get('endPeriodDelta')
	})

	let sublabel = i18n.get('charts.range.dataBy.detail', startLabel, endLabel)

	return { label, sublabel }
}

export default breakdownRangeLabels

breakdownRangeLabels.__filename__ = 'charts/breakdown-range-labels.js'
